import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { TreatmentItemRow } from '@/components/practitioner/TreatmentPlanEditor/TreatmentItemRow';
import { ExerciseRow } from '@/components/practitioner/TreatmentPlanEditor/ExerciseRow';
import type {
  EditorPlanExercise,
  EditorPlanItem,
} from '@/components/practitioner/TreatmentPlanEditor/types';
import type { Treatment } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import type { EditableTemplate } from './PlanTemplatesEditor';

function localizedName(nameEn: string, namePl: string): string {
  if (i18next.language === 'pl' && namePl.length > 0) {
    return namePl;
  }
  return nameEn;
}

let templateRowKeyCounter = 0;
function nextTemplateRowKey(): string {
  templateRowKeyCounter = templateRowKeyCounter + 1;
  return 'plan-template-row-' + templateRowKeyCounter;
}

interface PlanTemplateCardProps {
  template: EditableTemplate;
  libraryTreatments: Treatment[];
  isDisabled: boolean;
  hasIncompleteName: boolean;
  onChange: (template: EditableTemplate) => void;
  onRemove: () => void;
}

export function PlanTemplateCard({
  template,
  libraryTreatments,
  isDisabled,
  hasIncompleteName,
  onChange,
  onRemove,
}: PlanTemplateCardProps) {
  const { t } = useTranslation('plans');

  function addItem(treatment: Treatment) {
    const newItem: EditorPlanItem = {
      key: nextTemplateRowKey(),
      treatmentId: treatment.id,
      treatmentName: localizedName(treatment.nameEn, treatment.namePl),
      instructions: '',
      progress: null,
    };
    onChange({ ...template, items: [...template.items, newItem] });
  }

  function addExercise() {
    const newExercise: EditorPlanExercise = {
      key: nextTemplateRowKey(),
      name: '',
      instructions: '',
      frequency: '',
      compliance: null,
    };
    onChange({ ...template, exercises: [...template.exercises, newExercise] });
  }

  return (
    <Card padding="sm" shadow={false} className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <Input
            value={template.nameEn}
            placeholder={t('templates.nameEn')}
            hasError={hasIncompleteName && template.nameEn.trim().length === 0}
            disabled={isDisabled}
            onChange={function change(event) {
              onChange({ ...template, nameEn: event.target.value });
            }}
          />
          <Input
            value={template.namePl}
            placeholder={t('templates.namePl')}
            hasError={hasIncompleteName && template.namePl.trim().length === 0}
            disabled={isDisabled}
            onChange={function change(event) {
              onChange({ ...template, namePl: event.target.value });
            }}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={isDisabled}
          aria-label={t('templates.removeTemplate')}
          className="text-zoomuted hover:text-zooerrortext disabled:opacity-50 shrink-0"
        >
          <Icon name="Trash" />
        </button>
      </div>

      <div>
        <p className="text-xs font-bold text-zooindigo mb-2">{t('templates.items')}</p>
        <SearchableSelect
          inputId={'plan-template-item-picker-' + template.key}
          ariaLabel={t('templates.addItem')}
          placeholder={t('templates.addItem')}
          value=""
          isDisabled={isDisabled}
          options={libraryTreatments.map(function toOption(treatment) {
            return {
              value: treatment.id,
              label: localizedName(treatment.nameEn, treatment.namePl),
            };
          })}
          onChange={function pickTreatment(nextValue) {
            const treatment = libraryTreatments.find(function match(candidate) {
              return candidate.id === nextValue;
            });
            if (treatment !== undefined) {
              addItem(treatment);
            }
          }}
        />
        {template.items.length === 0 ? (
          <p className="text-xs text-zoomuted mt-2">{t('templates.noItems')}</p>
        ) : (
          <div className="space-y-2.5 mt-2">
            {template.items.map(function renderItem(item) {
              return (
                <TreatmentItemRow
                  key={item.key}
                  item={item}
                  isDisabled={isDisabled}
                  isReadOnly={false}
                  onChange={function change(updated) {
                    onChange({
                      ...template,
                      items: template.items.map(function replace(existing) {
                        return existing.key === updated.key ? updated : existing;
                      }),
                    });
                  }}
                  onRemove={function remove() {
                    onChange({
                      ...template,
                      items: template.items.filter(function keep(existing) {
                        return existing.key !== item.key;
                      }),
                    });
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-zooindigo">{t('templates.exercises')}</p>
          <Button
            variant="link"
            disabled={isDisabled}
            onClick={addExercise}
            className="gap-1.5 text-xs"
          >
            <Icon name="Plus" />
            {t('templates.addExercise')}
          </Button>
        </div>
        {template.exercises.length === 0 ? (
          <p className="text-xs text-zoomuted">{t('templates.noExercises')}</p>
        ) : (
          <div className="space-y-2.5">
            {template.exercises.map(function renderExercise(exercise) {
              return (
                <ExerciseRow
                  key={exercise.key}
                  exercise={exercise}
                  isDisabled={isDisabled}
                  isReadOnly={false}
                  onChange={function change(updated) {
                    onChange({
                      ...template,
                      exercises: template.exercises.map(function replace(existing) {
                        return existing.key === updated.key ? updated : existing;
                      }),
                    });
                  }}
                  onRemove={function remove() {
                    onChange({
                      ...template,
                      exercises: template.exercises.filter(function keep(existing) {
                        return existing.key !== exercise.key;
                      }),
                    });
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
